// SpaceContentUploadPage.jsx (React 19 + MUI, Toss-style)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../util/api";

// MUI
import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { notify } from "../../../../util/toast";
import { keyVisualApi } from "../api/keyVisualApi";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";

import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import LoadingDots from "../../../../component/common/LoadingDots";
import ActionButton from "../../../../component/common/ActionButton";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { ds } from "../../../../styles/designSystem";
import { runApiAction, serverMsgOr } from "../../../../util/apiFeedback";

export function SpaceContentUploadPage() {
    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    const [eventName, setEventName] = useState("");
    const [projectName, setProjectName] = useState("");
    const [projectOptions, setProjectOptions] = useState([]);
    const [location, setLocation] = useState("");
    const [locationType, setLocationType] = useState(""); // "실내" | "실외"
    const [files, setFiles] = useState([]);

    const [keywords, setKeywords] = useState([]);
    const [themes, setThemes] = useState([]);
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [selectedThemes, setSelectedThemes] = useState([]);

    const [manualKeyword, setManualKeyword] = useState("");
    const [manualTheme, setManualTheme] = useState("");

    const [loading, setLoading] = useState(false);

    // ✅ normalize: 앞/뒤 trim + "모든 공백 제거"
    const normalize = useCallback((s) => String(s ?? "").trim().replace(/\s+/g, ""), []);

    // ✅ "콤마로 구분" 입력 파서:  a, b c  -> ["a", "bc"] (공백 제거)
    const parseCommaParts = useCallback(
        (text) => {
            return String(text ?? "")
                .split(",")
                .map((x) => normalize(x))
                .filter(Boolean);
        },
        [normalize]
    );

    const uniqueByNormalize = useCallback(
        (arr) => {
            const map = new Map(); // normalized -> original(표시용)
            (arr || []).forEach((x) => {
                const nx = normalize(x);
                if (!nx) return;
                if (!map.has(nx)) map.set(nx, nx); // 표시도 normalize된 값으로 통일
            });
            return Array.from(map.values());
        },
        [normalize]
    );

    const fetchKeywords = useCallback(async () => {
        const data = await api.get(`${API_BASE}/space-content/keywords`);
        const raw = Array.isArray(data) ? data : (data?.data || []);
        const parsed = raw
            .flatMap((k) => String(k).split(","))
            .map((s) => normalize(s))
            .filter(Boolean);

        setKeywords(uniqueByNormalize(parsed).sort((a, b) => a.localeCompare(b, "ko")));
    }, [API_BASE, normalize, uniqueByNormalize]);

    const fetchThemes = useCallback(async () => {
        const data = await api.get(`${API_BASE}/space-content/themes`);
        const raw = Array.isArray(data) ? data : (data?.data || []);
        const parsed = raw
            .flatMap((t) => String(t).split(","))
            .map((s) => normalize(s))
            .filter(Boolean);

        setThemes(uniqueByNormalize(parsed).sort((a, b) => a.localeCompare(b, "ko")));
    }, [API_BASE, normalize, uniqueByNormalize]);

    useEffect(() => {
        (async () => {
            try {
                await Promise.all([fetchKeywords(), fetchThemes()]);
            } catch (e) {
                console.error(e);
                notify("키워드/테마 목록을 불러오지 못했습니다.", "error");
            }
        })();
    }, [fetchKeywords, fetchThemes]);

    // 프로젝트명 자동완성 옵션 — 2D/3D 통합 API
    const fetchProjectOptions = useCallback(async () => {
        try {
            const data = await keyVisualApi.listProjectNames();
            setProjectOptions(Array.isArray(data) ? data : (data?.data || []));
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        fetchProjectOptions();
    }, [fetchProjectOptions]);

    // ✅ 선택/해제 toggle (normalize 기준)
    const toggleNormalized = useCallback(
        (value, setList) => {
            const v = normalize(value);
            if (!v) return;

            setList((prev) => {
                const cur = (prev || []).map(normalize).filter(Boolean);
                const set = new Set(cur);
                if (set.has(v)) set.delete(v);
                else set.add(v);
                return Array.from(set);
            });
        },
        [normalize]
    );

    const selectedKeywordSet = useMemo(
        () => new Set((selectedKeywords || []).map(normalize).filter(Boolean)),
        [selectedKeywords, normalize]
    );
    const selectedThemeSet = useMemo(
        () => new Set((selectedThemes || []).map(normalize).filter(Boolean)),
        [selectedThemes, normalize]
    );

    // ✅ 수동 키워드/테마 추가 (콤마로 여러개, 공백 제거)
    const addManualParts = useCallback(
        (text, type) => {
            // 길이 제한 없음 — "빛", "물" 같은 한 글자 키워드가 실제로 쓰인다.
            // 빈 문자열은 parseCommaParts 가 이미 걸러낸다.
            const valid = parseCommaParts(text);
            if (valid.length === 0) return;

            if (type === "keyword") {
                setKeywords((prev) => uniqueByNormalize([...(prev || []), ...valid]).sort((a, b) => a.localeCompare(b, "ko")));
                setSelectedKeywords((prev) => uniqueByNormalize([...(prev || []), ...valid]));
                notify("키워드가 추가되었습니다.", "success");
            } else {
                setThemes((prev) => uniqueByNormalize([...(prev || []), ...valid]).sort((a, b) => a.localeCompare(b, "ko")));
                setSelectedThemes((prev) => uniqueByNormalize([...(prev || []), ...valid]));
                notify("행사 성격이 추가되었습니다.", "success");
            }
        },
        [parseCommaParts, uniqueByNormalize]
    );

    const handleManualKeywordKeyDown = useCallback(
        (e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key !== "Enter") return;
            e.preventDefault();

            const v = String(manualKeyword ?? "");
            if (!v.trim()) {
                setManualKeyword("");
                return;
            }

            addManualParts(v, "keyword");
            setManualKeyword("");
        },
        [addManualParts, manualKeyword]
    );

    const handleManualThemeKeyDown = useCallback(
        (e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key !== "Enter") return;
            e.preventDefault();

            const v = String(manualTheme ?? "");
            if (!v.trim()) {
                setManualTheme("");
                return;
            }

            addManualParts(v, "theme");
            setManualTheme("");
        },
        [addManualParts, manualTheme]
    );

    const fileInputRef = useRef(null);

    const handleFileChange = useCallback(
        (e) => {
            const selectedFiles = Array.from(e.target.files || []);
            if (selectedFiles.length === 0) return;

            const existing = new Set((files || []).map((f) => f.name));
            const uniqueFiles = selectedFiles.filter((f) => !existing.has(f.name));
            setFiles((prev) => [...(prev || []), ...uniqueFiles]);

            // input value reset (같은 파일 다시 선택 가능)
            e.target.value = "";
        },
        [files]
    );

    const removeFile = useCallback((index) => {
        setFiles((prev) => (prev || []).filter((_, i) => i !== index));
    }, []);

    const resetAll = useCallback(() => {
        setEventName("");
        setProjectName("");
        setLocation("");
        setLocationType("");
        setFiles([]);
        setSelectedKeywords([]);
        setSelectedThemes([]);
        setManualKeyword("");
        setManualTheme("");
        notify("초기화 완료", "success");
    }, []);

    const handleSubmit = useCallback(async () => {
        const ev = String(eventName || "").trim();
        const pj = String(projectName || "").trim();
        const loc = String(location || "").trim();
        const lt = String(locationType || "").trim();

        if (!ev || !loc || !lt) {
            notify("행사명, 장소명, 장소유형을 모두 입력해주세요.", "warning");
            return;
        }
        if (!pj) {
            notify("프로젝트명을 입력해주세요.", "warning");
            return;
        }
        if ((selectedKeywords || []).length === 0) {
            notify("키워드를 하나 이상 선택해주세요.", "warning");
            return;
        }
        if ((selectedThemes || []).length === 0) {
            notify("행사 성격을 하나 이상 선택해주세요.", "warning");
            return;
        }
        if ((files || []).length === 0) {
            notify("파일을 하나 이상 업로드해주세요.", "warning");
            return;
        }

        const formData = new FormData();
        formData.append("eventName", ev);
        formData.append("projectName", pj);
        formData.append("location", loc);
        formData.append("locationType", lt);

        // ✅ 전송도 normalize 기준으로 통일
        uniqueByNormalize(selectedKeywords).forEach((k) => formData.append("keywords", normalize(k)));
        uniqueByNormalize(selectedThemes).forEach((t) => formData.append("themes", normalize(t)));
        (files || []).forEach((file) => formData.append("files", file));

        await runApiAction({
            request: () => api.upload(`${API_BASE}/space-content/upload`, formData),
            errorMsg: serverMsgOr("업로드 실패(중복된 행사명이 있는지 확인해주세요)"),
            successMsg: "업로드 완료",
            setLoading,
            onSuccess: async () => {
                resetAll();
                await fetchProjectOptions();
            },
            onError: (e) => console.error(e),
        });
    }, [
        API_BASE,
        eventName,
        fetchProjectOptions,
        files,
        location,
        locationType,
        normalize,
        projectName,
        resetAll,
        selectedKeywords,
        selectedThemes,
        uniqueByNormalize,
    ]);

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} spacing={1.25}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={ds.typography.pageTitle}>
                        공간 콘텐츠 업로드
                    </Typography>
                    <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.4 }}>
                        키워드/테마 입력은 “콤마(,)로 구분” + “공백 제거” 기준으로 저장됩니다.
                    </Typography>
                </Box>

                <Box sx={{ flex: 1 }} />

                <ActionButton
                    variant="secondary"
                    startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={resetAll}
                    disabled={loading}
                    label="초기화"
                />
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    mt: 2,
                    p: 2.25,
                    ...ds.components.external.card,
                }}
            >
                <Stack spacing={2}>
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                            행사명
                        </Typography>
                        <TextField
                            value={eventName}
                            onChange={(e) => setEventName(e.target.value)}
                            placeholder="행사명을 입력하세요"
                            size="small"
                            fullWidth
                            disabled={loading}
                            sx={ds.components.external.inputSoft}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                            프로젝트명
                        </Typography>
                        <AppAutocomplete
                            freeSolo
                            options={projectOptions}
                            value={projectName}
                            inputValue={projectName}
                            onChange={(v) => setProjectName(v == null ? "" : String(v))}
                            onInputChange={(v) => setProjectName(v)}
                            disabled={loading}
                            placeholder="기존 프로젝트 선택 또는 새 이름 입력"
                            variant="outlined" disableUnderline={false}
                            textFieldSx={ds.components.external.inputSoft}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                            장소명
                        </Typography>
                        <TextField
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="장소명을 입력하세요"
                            size="small"
                            fullWidth
                            disabled={loading}
                            sx={ds.components.external.inputSoft}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                            장소유형
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {["실내", "실외"].map((v) => {
                                const selected = locationType === v;
                                return (
                                    <ActionButton
                                        key={v}
                                        variant={selected ? "primary" : "secondary"}
                                        startIcon={false}
                                        onClick={() => setLocationType(v)}
                                        sx={ds.components.external.pillButton}
                                        disabled={loading}
                                        label={v}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* 키워드 */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                    <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                                    키워드
                                </Typography>
                                <Typography sx={{ fontWeight: 600, fontSize: 13, color: "text.secondary" }}>
                                    클릭 선택/해제 · 입력은 콤마(,)로 구분 · 공백 제거 기준 (입력 후 Enter 로 키워드 등록 필요)
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                            {(keywords || []).map((k, i) => {
                                const nk = normalize(k);
                                const selected = selectedKeywordSet.has(nk);
                                return (
                                    <Chip
                                        key={`${nk}-${i}`}
                                        label={nk}
                                        clickable
                                        onClick={() => toggleNormalized(nk, setSelectedKeywords)}
                                        sx={selected ? ds.components.external.chipActive : ds.components.external.chip}
                                    />
                                );
                            })}
                        </Stack>

                        <TextField
                            value={manualKeyword}
                            onChange={(e) => setManualKeyword(e.target.value)}
                            onKeyDown={handleManualKeywordKeyDown}
                            placeholder="예) 코엑스,전시,부스 (Enter로 추가)"
                            size="small"
                            fullWidth
                            disabled={loading}
                            sx={ds.components.external.inputSoft}
                        />
                    </Box>

                    <Divider />

                    {/* 테마 */}
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                            행사 성격(테마)
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: "text.secondary", mb: 1 }}>
                            클릭 선택/해제 · 입력은 콤마(,)로 구분 · 공백 제거 기준 (입력 후 Enter 로 키워드 등록 필요)
                        </Typography>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                            {(themes || []).map((t, i) => {
                                const nt = normalize(t);
                                const selected = selectedThemeSet.has(nt);
                                return (
                                    <Chip
                                        key={`${nt}-${i}`}
                                        label={nt}
                                        clickable
                                        onClick={() => toggleNormalized(nt, setSelectedThemes)}
                                        sx={selected ? ds.components.external.chipSuccess : ds.components.external.chip}
                                    />
                                );
                            })}
                        </Stack>

                        <TextField
                            value={manualTheme}
                            onChange={(e) => setManualTheme(e.target.value)}
                            onKeyDown={handleManualThemeKeyDown}
                            placeholder="예) 전시,세미나,체험 (Enter로 추가)"
                            size="small"
                            fullWidth
                            disabled={loading}
                            sx={ds.components.external.inputSoft}
                        />
                    </Box>

                    <Divider />

                    {/* 파일 */}
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                            파일 업로드 (여러 파일 가능)
                        </Typography>

                        <ActionButton
                            variant="upload"
                            loading={loading}
                            onClick={() => fileInputRef.current?.click()}
                            label={loading ? "처리 중" : "파일 선택"}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            multiple
                            accept=".png,.jpg,.jpeg"
                            onChange={handleFileChange}
                            disabled={loading}
                        />

                        {(files || []).length > 0 && (
                            <Stack spacing={1} sx={{ mt: 1.5 }}>
                                {files.map((f, idx) => (
                                    <Paper
                                        key={`${f.name}-${idx}`}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 1.5,
                                            p: 1.25,
                                            borderColor: ds.colors.border,
                                            bgcolor: ds.colors.bg,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography noWrap sx={{ fontWeight: 700, fontSize: 14 }}>
                                                {f.name}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 800, fontSize: 13, color: "text.secondary" }}>
                                                {(f.size / (1024 * 1024)).toFixed(2)} MB
                                            </Typography>
                                        </Box>

                                        <ActionButton
                                            variant="delete"
                                            startIcon={<DeleteRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => removeFile(idx)}
                                            disabled={loading}
                                            label="제거"
                                        />
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    {/* 저장 */}
                    <Stack direction="row" justifyContent="flex-end">
                        <ActionButton
                            variant="save"
                            startIcon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={handleSubmit}
                            loading={loading}
                            label={loading ? "업로드 중" : "저장"}
                            sx={{ px: 3 }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            {/* 로딩 */}
            <Backdrop
                open={loading}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 10, bgcolor: "rgba(255,255,255,0.85)" }}
            >
                <LoadingDots label="업로드하고 있어요" />
            </Backdrop>

        </Container>
    );
}
